// hooks/useRegisterForm.ts
"use client";

import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  parsePhoneNumberFromString,
  CountryCode,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";
import { useSignup } from "@/hooks/auth-hook";
import { useRouter } from "next/navigation";
import Cookies from "js-cookie";

export const useRegisterForm = (setErrorMessage: any, isRtl: boolean) => {
  const { mutate, isPending } = useSignup();
  const router = useRouter();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);


  const countriesList = useMemo(() => {
    return getCountries()
      .map((iso) => ({
        iso,
        code: `+${getCountryCallingCode(iso)}`,
        // يمكنك إضافة خريطة أسماء هنا إذا أردت، أو الاكتفاء بالـ ISO
        name: new Intl.DisplayNames([isRtl ? "ar" : "en"], {
          type: "region",
        }).of(iso),
      }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [isRtl]);

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<any>({
    mode: "onChange",
    defaultValues: {
      countryCode: "SA",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });

  const passwordValue = watch("password", "");
  const confirmPasswordValue = watch("confirmPassword", "");
  const selectedCountry = watch("countryCode", "SA");
  const phoneValue = watch("phone", "");

  const passwordRules = [
    {
      label: isRtl ? "8+ أحرف" : "8+ characters",
      met: passwordValue.length >= 8,
    },
    {
      label: isRtl ? "حرف كبير" : "Uppercase",
      met: /[A-Z]/.test(passwordValue),
    },
    { label: isRtl ? "رقم" : "Number", met: /[0-9]/.test(passwordValue) },
    {
      label: isRtl ? "رمز خاص" : "Symbol",
      met: /[^A-Za-z0-9]/.test(passwordValue),
    },
  ];

  const isPasswordValid = passwordRules.every((rule) => rule.met);

  const onSubmit = (data: any) => {
    const phoneNumber = parsePhoneNumberFromString(
      data.phone,
      selectedCountry as CountryCode,
    );

    if (!phoneNumber || !phoneNumber.isValid()) {
      setErrorMessage({
        messages: isRtl ? "رقم الجوال غير صحيح" : "Invalid phone number",
        type: "error",
      });
      return;
    }

    mutate(
      {
        username: `${data.firstName} ${data.lastName}`,
        email: data.email,
        phone: phoneNumber.formatInternational(),
        password: data.password,
        role: "user",
      },
      {
        onSuccess: () => {
          Cookies.set("email", data.email, { expires: 1 / 288 });
          router.push("/auth/verify-email");
        },
        onError: (err: any) => {
          setErrorMessage({
            messages:
              err?.response?.data?.message || "Error during registration",
            type: "error",
          });
        },
      },
    );
  };

  return {
    register,
    handleSubmit,
    control,
    errors,
    isPending,
    passwordRules,
    isPasswordValid,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    selectedCountry,
    phoneValue,
    passwordValue,
    confirmPasswordValue,
    countriesList,
    onSubmit,
  };
};
